import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import { SendLoanDocumentsRequest } from '@matech/thebigpos-sdk'
import AttachmentIcon from '@mui/icons-material/Attachment'
import CloseIcon from '@mui/icons-material/Close'
import Dialog, { type DialogProps } from './Dialog'
import { useLoanContext } from '../../contexts/LoanContext'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import SendDocumentsForm from '../../forms/SendDocumentsForm/SendDocumentsForm'
import { getLoanDocumentContentUrl } from '../../services/client'
import { LayoutRow } from '../layout'
import { MediaFileProps } from '../MediaLibrary/types'
import Heading from '../Heading'
import IconButton from '../Button/IconButton'
import { eventTypes } from '../../services/constants'
import { downloadLoanDocument } from '../../services/helpers'
import { useMixpanel } from '../../hooks/useMixpanel'
import { Button } from '../Button'
import { useAlert } from '../../hooks'
import useErrorHandler from '../../hooks/useErrorHandler'
import { getErrorMessage } from '../../services/helper'
import { SendDocumentsFormValues } from '../../forms/SendDocumentsForm/types'

export type ModalSendDocumentsProps = {
	documents: MediaFileProps[]
	onSubmit?: () => void
	onCancel?: () => void
	onRemoveDocument?: (documentId: string) => void
} & Omit<DialogProps, 'onSubmit'>

const ModalSendDocuments = ({
	documents = [],
	onSubmit,
	onCancel,
	onRemoveDocument,
	...rest
}: ModalSendDocumentsProps) => {
	const { loanId, loan, isLoading } = useLoanContext()
	const { formatMessage } = useIntl()
	const mixpanel = useMixpanel()
	const { alert } = useAlert()
	const { handleError } = useErrorHandler()

	const loanUserOptions =
		loan?.userLoans
			.filter((user) => !!user.user?.id)
			.map((user) => ({
				label: `${user.user?.firstName} ${user.user?.lastName} (${user.user?.email})`,
				value: user.user?.id ?? '',
			})) ?? []

	const documentIDs = documents.map((doc) => doc.id)

	const sendLoanDocuments = useMutation({
		mutationFn: (data: SendLoanDocumentsRequest) => {
			return TheBigPOSApi.sendLoanDocuments(loanId, data)
		},
		onSuccess: () => {
			alert(
				formatMessage({
					id: 'send_documents_modal.alerts.send_success',
				}),
				{
					severity: 'success',
				}
			)
			onCancel?.()
		},
		onError: (error) => {
			const errorResponse = handleError(
				error,
				getErrorMessage(error) ??
					formatMessage({
						id: 'send_documents_modal.alerts.send_error',
					})
			)

			alert(errorResponse.message, {
				severity: 'error',
			})
		},
	})

	const handleSubmit = async (data: SendDocumentsFormValues) => {
		sendLoanDocuments.mutate(data)
		onSubmit?.()
	}

	const mutatedDocuments = useMemo(
		() =>
			documents.map((doc) => ({
				...doc,
				url:
					doc.url ??
					getLoanDocumentContentUrl({
						loanId,
						documentId: doc.id,
					}),
			})) ?? [],
		[documents, loanId]
	)

	const handleDownloadClick = (documentId: string) => {
		mixpanel.trackEvent(eventTypes.DOCUMENT_TEMPLATE_DOWNLOADED, {
			documentId,
		})
		downloadLoanDocument(loanId, documentId)
	}

	return (
		<Dialog
			title={formatMessage({
				id: 'send_documents_modal.heading',
			})}
			onClose={onCancel}
			fullWidth
			maxWidth="sm"
			{...rest}
		>
			<LayoutRow>
				<SendDocumentsForm
					documentIDs={documentIDs}
					loanUserOptions={loanUserOptions}
					onSubmit={handleSubmit}
					loading={isLoading || sendLoanDocuments.isPending}
				/>
			</LayoutRow>
			<LayoutRow>
				<Heading size="md" className="font-normal mb-2">
					{formatMessage({
						id: 'send_documents_modal.attachments_label',
					})}
				</Heading>
				<ul className="flex flex-wrap gap-2">
					{mutatedDocuments.map((doc) => (
						<li
							key={doc.id}
							className="flex gap-2 items-center rounded px-2 py-1 bg-slate-300"
						>
							<AttachmentIcon rotate={90} fontSize="small" />
							<Button
								text={doc.name}
								onClick={() => handleDownloadClick(doc.id)}
								variant="text"
								className="text-sm hover:!bg-transparent hover:!underline"
							/>
							{onRemoveDocument && (
								<IconButton
									className="ml-3"
									onClick={() => onRemoveDocument?.(doc.id)}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							)}
						</li>
					))}
				</ul>
			</LayoutRow>
		</Dialog>
	)
}

export default ModalSendDocuments
